import { createContext, useContext, useState, useEffect, ReactNode } from "react";
import { products, Product } from "@/data/products";

export interface CartItem extends Product {
  quantity: number;
}

interface CartContextType {
  cart: CartItem[];
  addToCart: (product: Product) => boolean;
  removeFromCart: (productId: number) => void;
  clearCart: () => void;
  totalPrice: number;
  showCart: boolean;
  setShowCart: (show: boolean) => void;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

const CART_STORAGE_KEY = "genai_cart";

export function CartProvider({ children }: { children: ReactNode }) {
  const [cart, setCart] = useState<CartItem[]>(() => {
    // Load cart from localStorage on initial render
    if (typeof window !== "undefined") {
      const saved = localStorage.getItem(CART_STORAGE_KEY);
      if (saved) {
        try {
          const parsed = JSON.parse(saved) as Array<Partial<CartItem>>;
          return parsed
            .map((item) => {
              const product = products.find((p) => p.id === item.id);
              if (!product) return null;
              return {
                ...product,
                quantity: item.quantity ?? 1,
              };
            })
            .filter((item): item is CartItem => Boolean(item));
        } catch {
          return [];
        }
      }
    }
    return [];
  });
  const [showCart, setShowCart] = useState(false);

  // Save cart to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(cart));
  }, [cart]);

  const addToCart = (product: Product): boolean => {
    const existing = cart.find((item) => item.id === product.id);
    if (existing) {
      return false; // Already in cart
    }
    setCart([...cart, { ...product, quantity: 1 }]);
    return true; // Successfully added
  };

  const removeFromCart = (productId: number) => {
    setCart(cart.filter((item) => item.id !== productId));
  };

  const clearCart = () => {
    setCart([]);
  };

  const totalPrice = cart.reduce(
    (sum, item) => sum + item.price * item.quantity,
    0
  );

  return (
    <CartContext.Provider
      value={{
        cart,
        addToCart,
        removeFromCart,
        clearCart,
        totalPrice,
        showCart,
        setShowCart,
      }}
    >
      {children}
    </CartContext.Provider>
  );
}

export function useCart() {
  const context = useContext(CartContext);
  if (context === undefined) {
    throw new Error("useCart must be used within a CartProvider");
  }
  return context;
}
